import React, { useState, useEffect, useCallback } from 'react';
import PrimaryNavBar from 'components/ui/PrimaryNavBar';
import Icon from 'components/AppIcon';
import CategoryCatalogEditor from './components/CategoryCatalogEditor';
import RiskThresholds from './components/RiskThresholds';
import ShockVolatilityParams from './components/ShockVolatilityParams';
import MarketFeedSettings from './components/MarketFeedSettings';
import AuditLogTab from './components/AuditLogTab';
import ImportConfigModal from './components/ImportConfigModal';
import { supabase } from '../../lib/supabase';
import { auditLog } from '../../services/metalguard';


const TABS = [
  { id: 'catalog', label: 'Catalogo de Categorias', shortLabel: 'Catalogo', icon: 'Tag', description: 'Categorias de chatarra por metal' },
  { id: 'risk', label: 'Umbrales de Riesgo', shortLabel: 'Riesgo', icon: 'ShieldAlert', description: 'Niveles de alerta y concentracion' },
  { id: 'shock', label: 'Shock y Volatilidad', shortLabel: 'Volatilidad', icon: 'Zap', description: 'Parametros de escenarios de shock' },
  { id: 'market', label: 'Feeds de Mercado', shortLabel: 'Mercado', icon: 'Radio', description: 'Overrides de benchmarks y EUR/USD' },
  { id: 'audit', label: 'Auditoría', shortLabel: 'Auditoría', icon: 'ClipboardList', description: 'Registro de cambios del sistema' },
];

function Toast({ message, type, onClose }) {
  const config = {
    success: { bg: 'bg-success', icon: 'CheckCircle', text: 'text-white' },
    error: { bg: 'bg-error', icon: 'XCircle', text: 'text-white' },
    info: { bg: 'bg-accent', icon: 'Info', text: 'text-white' },
  };
  const c = config?.[type] || config?.info;
  return (
    <div className={`fixed bottom-6 right-6 z-[200] flex items-center gap-3 px-5 py-3.5 rounded-lg shadow-xl ${c?.bg} ${c?.text} animate-slide-down`}
      style={{ minWidth: '280px', maxWidth: '400px', boxShadow: 'var(--shadow-xl)' }} role="alert">
      <Icon name={c?.icon} size={18} color="white" strokeWidth={2.5} />
      <span style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.875rem', fontWeight: '500' }}>{message}</span>
      <button onClick={onClose} className="ml-auto opacity-80 hover:opacity-100 transition-opacity" aria-label="Cerrar notificacion">
        <Icon name="X" size={16} color="white" strokeWidth={2.5} />
      </button>
    </div>
  );
}

export default function Settings() {
  const [activeTab, setActiveTab] = useState('catalog');
  const [toast, setToast] = useState(null);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // Refs to child save functions
  const [catalogSaveFn, setCatalogSaveFn] = useState(null);
  const [riskSaveFn, setRiskSaveFn] = useState(null);
  const [shockSaveFn, setShockSaveFn] = useState(null);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (activeTab === 'catalog' && catalogSaveFn) await catalogSaveFn();
      else if (activeTab === 'risk' && riskSaveFn) await riskSaveFn();
      else if (activeTab === 'shock' && shockSaveFn) await shockSaveFn();
      showToast('Configuracion guardada correctamente', 'success');
    } catch (err) {
      showToast('Error al guardar la configuracion', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');
      const userId = user?.id;

      const [metalsRes, categoriesRes, riskRes, volatilityRes, alertRulesRes, profileRes] = await Promise.all([
        supabase?.from('metals')?.select('*')?.eq('user_id', userId),
        supabase?.from('scrap_categories')?.select('*')?.eq('user_id', userId),
        supabase?.from('risk_thresholds')?.select('*')?.eq('user_id', userId)?.maybeSingle(),
        supabase?.from('volatility_settings')?.select('*')?.eq('user_id', userId),
        supabase?.from('alert_rules')?.select('*')?.eq('user_id', userId),
        supabase?.from('user_profiles')?.select('preferences')?.eq('user_id', userId)?.maybeSingle(),
      ]);

      const config = {
        exported_at: new Date()?.toISOString(),
        app_version: '1.0',
        user_email: user?.email,
        metals: metalsRes?.data || [],
        scrap_categories: categoriesRes?.data || [],
        risk_thresholds: riskRes?.data || null,
        volatility_settings: volatilityRes?.data || [],
        alert_rules: alertRulesRes?.data || [],
        preferences: profileRes?.data?.preferences || null,
      };

      const blob = new Blob([JSON.stringify(config, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const dateStr = new Date()?.toISOString()?.slice(0, 10);
      const a = document.createElement('a');
      a.href = url;
      a.download = `metalrecovery-config-${dateStr}.json`;
      document.body?.appendChild(a);
      a?.click();
      document.body?.removeChild(a);
      URL.revokeObjectURL(url);

      await auditLog('EXPORT_CONFIG', 'config', null, null, { exported_at: config?.exported_at }, 'settings');
      showToast('Configuración exportada correctamente', 'success');
    } catch (err) {
      showToast('Error al exportar la configuración', 'error');
    } finally {
      setExporting(false);
    }
  };

  const handleImportSuccess = (message) => {
    showToast(message, 'success');
    setRefreshKey(k => k + 1);
  };

  const activeTabData = TABS?.find((t) => t?.id === activeTab);

  return (
    <div className="min-h-screen" style={{ background: 'var(--color-background)' }}>
      <PrimaryNavBar />
      <div className="pt-[60px]">
        <div className="border-b border-border" style={{ background: 'var(--color-card)', boxShadow: 'var(--shadow-sm)' }}>
          <div className="max-w-screen-xl mx-auto px-4 lg:px-8">
            <div className="flex items-center justify-between py-5 gap-4">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-10 h-10 rounded-lg flex-shrink-0" style={{ background: 'var(--color-accent)', borderRadius: '10px' }}>
                  <Icon name="Settings" size={20} color="white" strokeWidth={2} />
                </div>
                <div>
                  <h1 className="text-foreground" style={{ fontFamily: '"Outfit", sans-serif', fontSize: '1.375rem', fontWeight: '700', lineHeight: 1.2 }}>Configuracion del Sistema</h1>
                  <p className="text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.8125rem' }}>{activeTabData?.description}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button onClick={() => setShowImport(true)} className="flex items-center gap-2 px-4 py-2 bg-muted border border-border rounded-lg text-foreground hover:bg-border transition-colors" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.875rem', fontWeight: '500' }}>
                  <Icon name="Upload" size={14} color="currentColor" strokeWidth={2} />
                  <span className="hidden sm:inline">Importar</span>
                </button>
                <button onClick={handleExport} disabled={exporting} className="flex items-center gap-2 px-4 py-2 bg-muted border border-border rounded-lg text-foreground hover:bg-border transition-colors disabled:opacity-60" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.875rem', fontWeight: '500' }}>
                  {exporting ? <Icon name="Loader2" size={14} color="currentColor" strokeWidth={2} className="animate-spin" /> : <Icon name="Download" size={14} color="currentColor" strokeWidth={2} />}
                  <span className="hidden sm:inline">{exporting ? 'Exportando...' : 'Exportar'}</span>
                </button>
                <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-5 py-2 rounded-lg text-white transition-colors disabled:opacity-60" style={{ background: 'var(--color-accent)', fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.875rem', fontWeight: '600' }}>
                  {saving ? <Icon name="Loader2" size={14} color="white" strokeWidth={2} className="animate-spin" /> : <Icon name="Save" size={14} color="white" strokeWidth={2.5} />}
                  {saving ? 'Guardando...' : 'Guardar cambios'}
                </button>
              </div>
            </div>
            <div className="flex items-center gap-0 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
              {TABS?.map((tab) => (
                <button key={tab?.id} onClick={() => setActiveTab(tab?.id)}
                  data-tour={tab?.id === 'catalog' ? 'settings-catalog-tab' : tab?.id === 'shock' ? 'settings-shock-tab' : tab?.id === 'risk' ? 'settings-risk-tab' : tab?.id === 'market' ? 'settings-feed-tab' : tab?.id === 'audit' ? 'settings-audit-tab' : undefined}
                  className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap flex-shrink-0 ${activeTab === tab?.id ? 'border-accent text-accent' : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'}`}
                  style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.875rem', fontWeight: activeTab === tab?.id ? '600' : '400' }}>
                  <Icon name={tab?.icon} size={15} color="currentColor" strokeWidth={activeTab === tab?.id ? 2.5 : 2} />
                  <span className="hidden md:inline">{tab?.label}</span>
                  <span className="md:hidden">{tab?.shortLabel}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="max-w-screen-xl mx-auto px-4 lg:px-8 py-8">
          {activeTab === 'catalog' && <CategoryCatalogEditor key={`catalog-${refreshKey}`} onRegisterSave={setCatalogSaveFn} onToast={showToast} />}
          {activeTab === 'risk' && <RiskThresholds key={`risk-${refreshKey}`} onRegisterSave={setRiskSaveFn} onToast={showToast} />}
          {activeTab === 'shock' && <ShockVolatilityParams key={`shock-${refreshKey}`} onRegisterSave={setShockSaveFn} onToast={showToast} />}
          {activeTab === 'market' && <MarketFeedSettings key={`market-${refreshKey}`} />}
          {activeTab === 'audit' && <AuditLogTab key={`audit-${refreshKey}`} />}
        </div>
      </div>
      {toast && <Toast message={toast?.message} type={toast?.type} onClose={() => setToast(null)} />}
      {showImport && <ImportConfigModal onClose={() => setShowImport(false)} onSuccess={handleImportSuccess} />}
    </div>
  );
}
